import React, { FC, useCallback, useMemo, useState } from 'react';
import { AggregationBucket, AggregationEntry, Aggregations } from '../../../models/Aggregations';
import { useFilterContext } from './FilterContext';
import CSSTransition from 'react-transition-group/CSSTransition';

import './AppliedFilters.scss';
import { Button, ButtonSize, ButtonTheme } from '../common/Button';
import { setDefaultFilter } from './FilterGroupContext';
import { Trans, useTranslation } from 'react-i18next';
import { mapFilterGroupKey } from '../common/translationMappers/filterbarLabelMapper';
import { TFunction } from 'i18next';
import { defaultBucketLabelMapper } from './ResponsiveFilterBar';

const SHOW_MORE_CUTOFF = 3;

type AppliedFiltersWithContextProps = {
    readonly bucketLabelMappers: { [key: string]: (t: TFunction, bucket: AggregationBucket) => string };
};

export const AppliedFiltersWithContext: FC<AppliedFiltersWithContextProps> = ({ bucketLabelMappers }) => {
    const filterContext = useFilterContext();

    const resetAppliedFilter = useCallback(
        (category: string, bucket: AggregationBucket) => {
            if (!filterContext.productFilter[category]) {
                return;
            }

            const [include, exclude] = setDefaultFilter(
                filterContext.productFilter[category].include ?? [],
                filterContext.productFilter[category].exclude ?? [],
                bucket.key,
                bucket.state
            );

            filterContext.changeFilter(category, include, exclude);
        },
        [filterContext]
    );

    const selectedFilters = useMemo(
        () =>
            Object.keys(filterContext.aggregations).filter(f =>
                bucketsHaveSomeActiveFilter(filterContext.aggregations[f].buckets)
            ),
        [filterContext.aggregations]
    );

    if (Object.keys(selectedFilters).length === 0) {
        return null;
    }

    return (
        <div className="filter-group">
            <AppliedFilters
                collapsible={true}
                handleConcreteFilterReset={resetAppliedFilter}
                filters={filterContext.aggregations}
                resetAllFilters={filterContext.resetAllFilters}
                bucketLabelMappers={bucketLabelMappers}
            />
        </div>
    );
};

const bucketsHaveSomeActiveFilter = (buckets: ReadonlyArray<AggregationBucket> | undefined): boolean => {
    if (!buckets) {
        return false;
    }

    return buckets.some(bucket => !!bucket.state || bucketsHaveSomeActiveFilter(bucket.buckets));
};

type AppliedFiltersProps = {
    readonly filters: Aggregations;
    readonly disableRemoveFilterForKeys?: ReadonlyArray<string>;
    readonly handleConcreteFilterReset: (key: string, bucket: AggregationBucket) => void;
    readonly resetAllFilters?: () => void;
    readonly collapsible: boolean;
    readonly bucketLabelMappers: { [key: string]: (t: TFunction, bucket: AggregationBucket) => string };
};

export const AppliedFilters: FC<AppliedFiltersProps> = ({
    filters,
    disableRemoveFilterForKeys = [],
    handleConcreteFilterReset,
    resetAllFilters,
    collapsible,
    bucketLabelMappers
}) => {
    const [expanded, setExpanded] = useState(true);
    const { t } = useTranslation();

    const selectedFilters = useMemo(
        () => Object.keys(filters).filter(f => bucketsHaveSomeActiveFilter(filters[f].buckets)),
        [filters]
    );

    if (!collapsible && Object.keys(selectedFilters).length === 0) {
        return null;
    }

    const appliedFiltersCount = selectedFilters.length;

    return (
        <div className="filter-tile-root applied-filters">
            <span className="filter-title" onClick={() => setExpanded(e => !collapsible || !e)}>
                {t('components.filterBar.appliedFilters')}
                {collapsible && (
                    <>
                        <span className="count">({appliedFiltersCount})</span>
                        {appliedFiltersCount > 0 && resetAllFilters && (
                            <Button
                                onClick={resetAllFilters}
                                className="reset-all-button"
                                text={t('components.filterBar.resetAll')}
                                theme={ButtonTheme.NEUTRAL}
                                size={ButtonSize.SMALL}
                            />
                        )}
                        {appliedFiltersCount > 0 && (
                            <i className="material-icons expand-icon">{expanded ? 'expand_less' : 'expand_more'}</i>
                        )}
                    </>
                )}
            </span>
            <CSSTransition
                mountOnEnter={true}
                unmountOnExit={true}
                in={expanded}
                className="applied-filters"
                classNames="applied-filters"
                timeout={200}
            >
                <div>
                    {selectedFilters.map(category => {
                        const filter = filters[category];
                        const bucketLabelMapper = (bucket: AggregationBucket) =>
                            bucketLabelMappers[category]
                                ? bucketLabelMappers[category](t, bucket)
                                : defaultBucketLabelMapper(t, category, bucket.key, bucket.value);

                        return (
                            <div key={category}>
                                <AppliedFilterList
                                    cutoffThreshold={collapsible ? Number.MAX_SAFE_INTEGER : SHOW_MORE_CUTOFF}
                                    aggregationEntry={filter}
                                    handleConcreteFilterReset={bucket => handleConcreteFilterReset(category, bucket)}
                                    disableRemoveFilterForKeys={disableRemoveFilterForKeys}
                                    bucketLabelMapper={bucketLabelMapper}
                                />
                            </div>
                        );
                    })}
                </div>
            </CSSTransition>
        </div>
    );
};

const buildAppliedFilterListEntries = (
    buckets: ReadonlyArray<AggregationBucket> | undefined,
    bucketLabelMapper: (bucket: AggregationBucket) => string,
    labelPrefix?: string
): ReadonlyArray<{ label: string; state: string; key: string; bucket: AggregationBucket }> => {
    if (!buckets) {
        return [];
    }

    return buckets.flatMap(bucket => {
        const title = bucketLabelMapper(bucket);
        const label = labelPrefix ? `${labelPrefix} > ${title}` : title;
        const thisEntry = !!bucket.state
            ? [
                  {
                      label,
                      state: bucket.state,
                      key: bucket.key,
                      bucket
                  }
              ]
            : [];
        const subEntries = buildAppliedFilterListEntries(bucket.buckets, bucketLabelMapper, label);

        return [...thisEntry, ...subEntries];
    });
};

type AppliedFilterListProps = {
    readonly aggregationEntry: AggregationEntry;
    readonly disableRemoveFilterForKeys: ReadonlyArray<string>;
    readonly handleConcreteFilterReset: (bucket: AggregationBucket) => void;
    readonly cutoffThreshold: number;
    readonly bucketLabelMapper: (bucket: AggregationBucket) => string;
};

const AppliedFilterList: FC<AppliedFilterListProps> = ({
    aggregationEntry,
    disableRemoveFilterForKeys,
    handleConcreteFilterReset,
    cutoffThreshold,
    bucketLabelMapper
}) => {
    const [expanded, setExpanded] = useState(false);
    const { t } = useTranslation();

    const appliedFiltersSorted = [...buildAppliedFilterListEntries(aggregationEntry.buckets, bucketLabelMapper)].sort(
        (a, b) => {
            return `${b.state} ${b.label}`.localeCompare(`${a.state} ${a.label}`);
        }
    );

    const filterGroupKey = mapFilterGroupKey(aggregationEntry.value);
    const appliedFiltersTotal = appliedFiltersSorted.length;

    return (
        <div>
            <div className="applied-filter-title">
                {t(`components.filterBar.filterGroup.${filterGroupKey}`, {
                    defaultValue: aggregationEntry.value
                })}
            </div>
            {appliedFiltersSorted.map((filter, i) => {
                if (expanded || i < cutoffThreshold) {
                    return (
                        <AppliedFilterListEntry
                            key={filter.key}
                            disabled={disableRemoveFilterForKeys.includes(filter.key)}
                            label={filter.label}
                            state={filter.state}
                            onFilterReset={() => handleConcreteFilterReset(filter.bucket)}
                        />
                    );
                }

                return null;
            })}
            {appliedFiltersTotal > cutoffThreshold && (
                <a
                    className="applied-more-link"
                    onClick={() => {
                        setExpanded(e => !e);
                    }}
                >
                    {expanded
                        ? t('components.filterBar.hide')
                        : t('components.filterBar.andMoreWithCount', {
                              amount: appliedFiltersTotal - 3
                          })}
                    <i className="material-icons small">{expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
                </a>
            )}
        </div>
    );
};

type AppliedFilterListEntryProps = {
    readonly label: string;
    readonly state: string;
    readonly disabled: boolean;
    readonly onFilterReset: () => void;
};

const AppliedFilterListEntry: FC<AppliedFilterListEntryProps> = ({ label, state, disabled, onFilterReset }) => {
    return (
        <div>
            <a onClick={() => !disabled && onFilterReset()}>
                <i className="material-icons up icn-close">close</i>
            </a>
            {state === 'exclude' ? (
                <Trans
                    i18nKey={'components.filterBar.excludeWithFilter'}
                    components={[<i />]}
                    values={{
                        filter: label
                    }}
                />
            ) : (
                <Trans
                    i18nKey={'components.filterBar.onlyWithFilter'}
                    components={[<i />]}
                    values={{
                        filter: label
                    }}
                />
            )}
        </div>
    );
};
